import {AuiIf, ComposerPrimitive, useAui} from '@assistant-ui/react';
import {IconButton, type IconName, TagGroup, truncate} from '@wandb/components';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  createEditor,
  type Descendant,
  Editor,
  Element as SlateElement,
  Text as SlateText,
  Transforms,
} from 'slate';
import {withHistory} from 'slate-history';
import {ReactEditor, withReact} from 'slate-react';

import {useDebounceState} from '../../util/hooks';
import {useNavContext} from '../NavContextProvider';
import {COMPOSER_INITIAL_VALUE} from './constants';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {encodeMention, encodePromptContext} from './utils';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentEditor} from './WBAgentEditor';
import {
  useRunMentionSearch,
  WBAgentRunMentionDropdown,
} from './WBAgentRunMentionDropdown';

const ComposerAction = ({
  hasProjectContext,
  isEmpty,
  onSend,
}: {
  hasProjectContext: boolean;
  isEmpty: boolean;
  onSend: () => void;
}) => {
  return (
    <div className="flex">
      <AuiIf condition={s => !s.thread.isRunning}>
        <IconButton
          isDisabled={isEmpty || !hasProjectContext}
          aria-label="send message"
          variant="primary"
          icon="up"
          onPress={onSend}
        />
      </AuiIf>
      <AuiIf condition={s => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <IconButton aria-label="cancel query" icon="agent-stop" />
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

// Helper to convert Slate document tree into a flat string
const serializeSlateValue = (nodes: Descendant[]): string => {
  return nodes
    .map(node => {
      if (SlateText.isText(node)) {
        return node.text;
      }
      if (node.type === 'run-mention') {
        return encodeMention(node.name, node.displayName);
      }
      if (node.children) {
        return serializeSlateValue(node.children);
      }
      return '';
    })
    .join('');
};

const CUSTOM_VOID_TYPES = new Set(['run-mention']);

export const WBAgentComposer = () => {
  const aui = useAui();
  const {params} = useNavContext();
  const hasProjectContext = !!params.entityName && !!params.projectName;
  const nightModeRevertFilter = useNightModeRevertFilter();

  const [isComposerEmpty, setIsComposerEmpty] = useState(true);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [_liveQuery, debouncedMentionQuery, setMentionQuery] = useDebounceState(
    '',
    300
  );

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const mentionActive =
    isMentionOpen && !!params.projectName && !!params.entityName;
  const mentionRuns = useRunMentionSearch(
    params.entityName ?? '',
    params.projectName ?? '',
    mentionActive ? debouncedMentionQuery : ''
  );
  const clampedHighlightedIndex =
    mentionRuns.length === 0
      ? 0
      : Math.min(highlightedIndex, mentionRuns.length - 1);

  const selectedRuns = useWBAgentContextStore(s => s.selectedRuns);
  const removeRun = useWBAgentContextStore(s => s.removeRun);
  const syncProject = useWBAgentContextStore(s => s.syncProject);
  const composerDraft = useWBAgentContextStore(s => s.composerDraft);
  const setComposerDraft = useWBAgentContextStore(s => s.setComposerDraft);

  const editor = useMemo(() => {
    const e = withHistory(withReact(createEditor()));
    const {isInline, isVoid} = e;
    e.isInline = element => {
      return element.type != null && CUSTOM_VOID_TYPES.has(element.type)
        ? true
        : isInline(element);
    };
    e.isVoid = element => {
      return element.type != null && CUSTOM_VOID_TYPES.has(element.type)
        ? true
        : isVoid(element);
    };
    return e;
  }, []);

  // Clear runs context if user changes projects
  useEffect(() => {
    syncProject(params.projectName);
  }, [params.projectName, syncProject]);

  const getAtMatch = (editor: Editor) => {
    const {selection} = editor;
    if (!selection) {
      return null;
    }
    const [node] = Editor.node(editor, selection.anchor);
    if (!SlateText.isText(node)) {
      return null;
    }
    const textBeforeCursor = node.text.slice(0, selection.anchor.offset);
    return textBeforeCursor.match(/@([\w-]*)$/);
  };

  const handleSlateChange = useCallback(() => {
    const voidNodeEntries = [
      ...Editor.nodes(editor, {
        at: [],
        match: n =>
          SlateElement.isElement(n) &&
          n.type != null &&
          CUSTOM_VOID_TYPES.has(n.type),
      }),
    ];
    setIsComposerEmpty(
      Editor.string(editor, []).length === 0 && voidNodeEntries.length === 0
    );
    setComposerDraft(editor.children);

    const atMatch = getAtMatch(editor);
    if (atMatch) {
      setIsMentionOpen(true);
      setMentionQuery(atMatch[1]);
      setHighlightedIndex(0);
    } else {
      setIsMentionOpen(false);
      setMentionQuery('');
    }
  }, [editor, setMentionQuery, setComposerDraft]);

  const handleRunSelect = (displayName: string, name: string) => {
    const atMatch = getAtMatch(editor);
    if (atMatch) {
      Transforms.delete(editor, {
        distance: atMatch[0].length,
        unit: 'character',
        reverse: true,
      });
    }
    Transforms.insertNodes(editor, {
      type: 'run-mention',
      displayName,
      name,
      children: [{text: ''}],
    });
    Transforms.move(editor);
    Transforms.insertText(editor, ' ');

    setIsMentionOpen(false);
    setMentionQuery('');
    ReactEditor.focus(editor);
  };

  const buildMessage = (): string => {
    const userText = serializeSlateValue(editor.children);
    return encodePromptContext(selectedRuns, userText);
  };

  const clearComposer = () => {
    editor.children = COMPOSER_INITIAL_VALUE;
    Transforms.deselect(editor);
    editor.onChange();
  };

  const handleSend = () => {
    const message = buildMessage();
    aui.thread().append(message);
    clearComposer();
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      const inlineVoid = Editor.above(editor, {
        match: n =>
          SlateElement.isElement(n) &&
          Editor.isVoid(editor, n) &&
          Editor.isInline(editor, n),
      });
      if (inlineVoid) {
        e.preventDefault();
        Editor.deleteBackward(editor);
        return;
      }
    }
    if (isMentionOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setIsMentionOpen(false);
        setMentionQuery('');
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (mentionRuns.length > 0) {
          setHighlightedIndex(i => (i + 1) % mentionRuns.length);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (mentionRuns.length > 0) {
          setHighlightedIndex(i => (i > 0 ? i - 1 : mentionRuns.length - 1));
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const run = mentionRuns[clampedHighlightedIndex];
        if (run?.displayName) {
          handleRunSelect(run.displayName, run.name);
        }
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const truncProjectName = params.projectName
    ? truncate(params.projectName, {pos: 'end', maxChars: 30}).value
    : undefined;

  return (
    <div className="relative">
      <ComposerPrimitive.Root className="flex flex-col gap-8 rounded border border-moon-250 bg-white p-12">
        {params.projectName && (
          <div style={{filter: nightModeRevertFilter}}>
            <TagGroup
              aria-label="project name tag"
              items={[
                {
                  id: 'project name',
                  icon: 'folder-project' as IconName,
                  textValue: truncProjectName ?? params.projectName,
                  removable: false,
                },
                ...selectedRuns.map(run => ({
                  id: run.name,
                  icon: 'running-repeat' as IconName,
                  textValue: truncate(run.displayName, {
                    pos: 'end' as const,
                    maxChars: 29,
                  }).value,
                })),
              ]}
              onRemove={key => {
                removeRun(String(key));
              }}
            />
          </div>
        )}
        <WBAgentEditor
          editor={editor}
          hasProjectContext={hasProjectContext}
          initialValue={composerDraft}
          onChange={handleSlateChange}
          onKeyDown={handleComposerKeyDown}
        />
        {mentionActive && (
          <WBAgentRunMentionDropdown
            runs={mentionRuns}
            highlightedIndex={clampedHighlightedIndex}
            onHover={setHighlightedIndex}
            onSelect={handleRunSelect}
          />
        )}
        <div className="flex w-full justify-end">
          <ComposerAction
            hasProjectContext={hasProjectContext}
            isEmpty={isComposerEmpty && selectedRuns.length === 0}
            onSend={handleSend}
          />
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
};
